import { Component, EventEmitter, Input, Output } from '@angular/core';
import { AlertFeedback, AlertVerdictSubmission } from 'app/shared/models/alert-feedback.model';
import { WaitingAlert } from 'app/shared/models/waiting-alert.model';

/**
 * One anomaly waiting for review: what moved, by how much, who raised it, and what to do about it.
 *
 * Composable into the tray or any future inbox surface — it holds no tray state of its own and
 * records nothing, it reports the reviewer's verdict upwards and lets its host own the writing.
 *
 * The drivers and the suggested path sit behind a disclosure, so a tray of anomalies reads as a
 * list of headlines and opens into detail only where the reviewer looks.
 */
@Component({
  selector: 'gains-alert-card',
  standalone: false,
  templateUrl: './gains-alert-card.component.html',
  styleUrl: './gains-alert-card.component.scss'
})
export class GainsAlertCardComponent {
  @Input() alert?: WaitingAlert;

  /** The verdict already recorded against this alert, if any. */
  @Input() feedback?: AlertFeedback;

  /**
   * Whether the owning agent can be asked about this anomaly.
   *
   * Taken from the host rather than resolved here: availability lives on the agent catalogue, and
   * this card is presentational — it renders the same in the tray, in a spec, or anywhere else.
   */
  @Input() canAskDeo = true;

  @Output() verdictGiven$ = new EventEmitter<AlertVerdictSubmission>();

  @Output() askDeo$ = new EventEmitter<WaitingAlert>();

  /** Whether the drivers and suggested path are showing. */
  isDetailExpanded = false;

  get hasDetail(): boolean {
    return !!this.alert?.drivers?.length || !!this.alert?.suggestedPath?.steps?.length;
  }

  get detailToggleLabel(): string {
    return this.isDetailExpanded ? 'Hide why this was flagged' : 'Why this was flagged';
  }

  get detailToggleIcon(): string {
    return this.isDetailExpanded ? 'assets/icons/arrow-up.svg' : 'assets/icons/arrow-down.svg';
  }

  toggleDetail(): void {
    this.isDetailExpanded = !this.isDetailExpanded;
  }

  onVerdictGiven(submission: AlertVerdictSubmission): void {
    this.verdictGiven$.emit(submission);
  }

  onAskDeo(): void {
    if (this.alert) {
      this.askDeo$.emit(this.alert);
    }
  }
}
